import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ShieldCheck, WifiOff, ScanLine } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && !submitting) navigate("/", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("¡Hola de vuelta!");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pude iniciar sesión");
      setSubmitting(false);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative bg-pampa-dawn noise overflow-hidden">
      {/* Top brand strip */}
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-6 sm:pt-8 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Logo variant="mark" className="h-10 w-10 shrink-0" />
          <div className="leading-tight">
            <div className="font-heading font-semibold text-foreground text-[15px] tracking-tight">
              <span>Cuenta</span>
              <span className="text-[hsl(var(--primary))] ml-1">Vaca</span>
            </div>
            <div className="text-[10.5px] text-muted-foreground uppercase tracking-[0.1em]">Rimoly South · LATAM</div>
          </div>
        </div>
        <a href="#" className="hidden sm:inline-flex text-[12px] font-mono text-muted-foreground hover:text-foreground transition-colors">v1.0</a>
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12 grid lg:grid-cols-[1.05fr,0.95fr] gap-10 lg:gap-16 items-center">
        {/* Editorial hero */}
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1 text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--state-present))]" /> Trazabilidad ganadera · offline-first
          </div>

          {/* Brand wordmark — sólo visible en mobile, ancla emocional con la marca */}
          <div className="lg:hidden mb-5 flex items-center gap-3">
            <Logo variant="mark" className="h-14 w-14 shrink-0" />
            <div className="leading-tight">
              <div className="font-heading text-[22px] font-semibold tracking-tight">
                <span>Cuenta</span>
                <span className="text-[hsl(var(--primary))] ml-1.5">Vaca</span>
              </div>
              <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground mt-0.5">
                Inventario · Identifica · Controla
              </div>
            </div>
          </div>

          <h1 className="font-editorial text-[40px] sm:text-[56px] lg:text-[64px] leading-[1.02] tracking-tight text-foreground">
            La hacienda,<br />
            <span className="italic">cabeza por cabeza</span>.
          </h1>
          <p className="mt-5 text-[15px] sm:text-base text-muted-foreground max-w-md leading-relaxed">
            Inventario y trazabilidad por RFID para el campo real. Funciona sin señal, sincroniza cuando vuelve.
          </p>

          {/* Trust strip */}
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[
              { Icon: WifiOff,     k: "100% offline",     v: "trabaja sin señal" },
              { Icon: ScanLine,    k: "RFID + manual",    v: "lector físico o carga manual" },
              { Icon: ShieldCheck, k: "Datos del campo",  v: "export CSV · backups" },
            ].map((t) => (
              <div key={t.k} className="rounded-[calc(var(--radius)-0.25rem)] border border-border bg-card/70 backdrop-blur-sm p-3">
                <t.Icon size={15} className="text-primary" />
                <div className="mt-2 text-[12px] font-heading font-semibold text-foreground tracking-tight">{t.k}</div>
                <div className="text-[10.5px] text-muted-foreground mt-0.5">{t.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Auth card */}
        <div className="relative w-full max-w-md justify-self-center lg:justify-self-end">
          <div className="absolute -inset-2 -z-10 rounded-[calc(var(--radius)+0.4rem)] bg-[hsl(var(--brand-ochre)/0.18)] blur-2xl" aria-hidden />
          <div className="relative rounded-[var(--radius)] border border-border bg-card p-6 sm:p-7 shadow-paper-hover">
            {/* Hero logo dentro de la card — sólo desktop */}
            <div className="hidden lg:flex items-center justify-center mb-5">
              <Logo variant="name" className="h-16" alt="Cuenta Vaca" />
            </div>
            <div className="mb-5">
              <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Acceso al sistema</div>
              <h2 className="font-heading text-[22px] font-semibold tracking-tight text-foreground mt-1">Iniciar sesión</h2>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[12px] text-muted-foreground">Email o usuario</Label>
                <Input id="email" data-testid="login-email-input" type="text" autoCapitalize="none" autoCorrect="off" required value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="tu@finca.com o tu usuario" className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[12px] text-muted-foreground">Contraseña</Label>
                <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="••••••••" className="h-11" />
              </div>
              <Button data-testid="login-submit-button" type="submit" disabled={loading} size="lg" className="w-full">
                {loading ? "Entrando…" : "Iniciar sesión"}
              </Button>
            </form>
            <div className="mt-5 text-center text-[13px] text-muted-foreground">
              ¿Primera vez?{" "}
              <Link to="/registrarse" className="text-primary font-medium hover:underline underline-offset-4" data-testid="login-go-register-link">Creá tu cuenta</Link>
            </div>
          </div>
          <p className="text-[10.5px] font-mono text-muted-foreground text-center mt-3 tracking-[0.08em]">
            Rimoly South INC · 2026 · Cuenta Vaca
          </p>
        </div>
      </div>
    </div>
  );
}
